import { Routes } from '@angular/router';
import {
  LayoutProviderService,
  WorkspaceLayoutComponent,
  ClearParamsGuard
} from '@nrwl/common-lib';
import {
  AuthenticationGuardService as AuthGuard,
  RefreshEntityCacheGuard
} from '@nrwl/fusion-authentication';
import { FinalTestReportsComponent, TwampReportsComponent } from '@nrwl/final-test-reports';
import { PerspectiveLayoutComponent } from './fusion-ui/perspective.layout/perspective.layout.component';

import { UserAdministrationComponent, UserProfileComponent } from '@nrwl/user-administration';

import { TestSetupRouteStarterComponent } from '@nrwl/test-setup-router';
import { RolesAuthenticationGuard } from './fusion-ui/services/guard/roles.authentication.guard';
import { ApplicationsPageComponent } from '@nrwl/application-management';
import { WizardRouteStarterComponent } from '@nrwl/wizard';
import { AboutComponent } from './fusion-ui/about/about.component';
import { LicensesPageComponent } from '@nrwl/licenses';
import { UserComponent } from './fusion-ui/user/user.component';
import {
  DatasourceEntityComponent,
  EndpointsManagementComponent,
  UploadsSettingsComponent,
  DownloadsSettingsComponent,
  ReportsSettingComponent
} from '@nrwl/datasource-management';
import { TestListPageComponent } from '@nrwl/test-list-management';
import { publicWebAccessComponent } from '@nrwl/common-lib';
import { LogsComponent } from '@nrwl/common-lib';
export const appRoutes: Routes = [
  { path: '', redirectTo: '/signin', pathMatch: 'full' },
  {
    path: 'perspective/dfvhu',
    redirectTo: '/perspective/dfvhu/(dashboard:landing-page)?#dashboard',
    pathMatch: 'full'
  },
  {
    path: 'landing-page',
    component: WorkspaceLayoutComponent,
    canActivate: [AuthGuard],
    resolve: {
      layout: LayoutProviderService
    }
  },
  {
    path: 'test',
    loadChildren: () => import('@nrwl/test-client').then(m => m.testClientRoutes)
  },

  {
    path: 'perspective/:id',
    component: PerspectiveLayoutComponent,
    canActivate: [AuthGuard, RefreshEntityCacheGuard],
    children: [
      {
        path: ':wizardId',
        outlet: 'wizard',
        component: WizardRouteStarterComponent,
        canActivate: [RolesAuthenticationGuard],
        canDeactivate: [ClearParamsGuard]
      },
      {
        path: ':testId',
        outlet: 'testSetup',
        component: TestSetupRouteStarterComponent,
        canActivate: [RolesAuthenticationGuard],
        canDeactivate: [ClearParamsGuard]
      },
      {
        path: 'applications',
        outlet: 'dashboard',
        component: ApplicationsPageComponent,
        pathMatch: 'full'
      },
      {
        path: 'test-list-management',
        outlet: 'dashboard',
        component: TestListPageComponent,
        pathMatch: 'full'
      },
      { path: 'licenses', outlet: 'dashboard', component: LicensesPageComponent },
      { path: 'about', outlet: 'dashboard', component: AboutComponent },
      { path: 'public-web-access', outlet: 'dashboard', component: publicWebAccessComponent },
      {
        path: 'user-administration',
        outlet: 'dashboard',
        component: UserAdministrationComponent,
        canActivate: [RolesAuthenticationGuard]
      },
      {
        path: 'reports/:type/:id',
        outlet: 'dashboard',
        component: FinalTestReportsComponent,
        canActivate: [RolesAuthenticationGuard]
      },
      {
        path: 'twamp-test-reports',
        outlet: 'dashboard',
        component: TwampReportsComponent,
        canActivate: [RolesAuthenticationGuard]
      },
      {
        path: '',
        outlet: 'dashboard',
        component: UserComponent,
        children: [
          { path: '', redirectTo: 'profile', pathMatch: 'full' },
          { path: 'profile', component: UserProfileComponent },
          {
            path: 'profile/:userName',
            component: UserProfileComponent,
            canActivate: [RolesAuthenticationGuard]
          }
        ]
      },
      {
        path: 'datasource-management',
        outlet: 'dashboard',
        component: DatasourceEntityComponent,
        pathMatch: 'full'
      },
      {
        path: 'endpoints-management',
        outlet: 'dashboard',
        component: EndpointsManagementComponent,
        pathMatch: 'full'
      },
      {
        path: 'uploads',
        outlet: 'dashboard',
        component: UploadsSettingsComponent,
        pathMatch: 'full'
      },
      {
        path: 'downloads',
        outlet: 'dashboard',
        component: DownloadsSettingsComponent,
        pathMatch: 'full'
      },
      {
        path: 'reports-setting',
        outlet: 'dashboard',
        component: ReportsSettingComponent,
        pathMatch: 'full'
      },
      {
        path: 'logs',
        outlet: 'dashboard',
        component: LogsComponent,
        pathMatch: 'full'
      },
      {
        path: 'application-summary',
        outlet: 'dashboard',
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        loadChildren: () =>
          import('@nrwl/application-summary').then(m => m.ApplicationSummaryModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'voip-summary',
        outlet: 'dashboard',
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        loadChildren: () => import('@nrwl/voip-summary').then(m => m.VoipSummaryModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'landing-page',
        outlet: 'dashboard',
        loadComponent: () =>
          import('@nrwl/test-list-management').then(m => m.TestListPageComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'template-management',
        outlet: 'dashboard',
        loadComponent: () => import('@nrwl/template-management').then(m => m.TemplateTabsComponent),
        canActivate: [AuthGuard]
      },
      {
        path: 'truespeed-page',
        outlet: 'dashboard',
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        loadChildren: () => import('@nrwl/truespeed-details').then(m => m.TruespeedDetailsModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'y1564-scheduled-page',
        outlet: 'dashboard',
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        loadChildren: () => import('@nrwl/y1564-details').then(m => m.Y1564DetailsModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'test-running',
        outlet: 'dashboard',
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        loadChildren: () => import('@nrwl/test-running').then(m => m.TestRunningModule),
        canActivate: [AuthGuard]
      },
      {
        path: 'multi-step-summary',
        outlet: 'dashboard',
        // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
        loadChildren: () => import('@nrwl/multi-step-summary').then(m => m.MultiStepSummaryModule),
        canActivate: [AuthGuard]
      }
    ]
  }
  //maybe create a NotFound component with an adequate view for '**' path
  //  {path: '**', redirectTo: 'workspace'}
];
